import React from 'react';
import ReactEcharts from 'echarts-for-react';
import { toast } from 'react-toastify';
import ColorPicker from 'src/components/Colorpicker/index-beta';
import { useChartManagement } from 'src/managers/chartManager/stateManagement/management';

const DOUBLE_CLICK_DELAY_MS = 450;

/**
 * TreeMapChart de correlação.
 *
 * Segue o mesmo contrato dos demais gráficos integrados ao ChartManager:
 *   - `this.manager` orquestra ciclo de cores, highlit e color picker
 *   - `this.colorPickerRef` aponta para o componente do color picker
 *   - `this.configurationError` carrega o erro vindo do ThemeManager
 *
 * Detecção do duplo-clique: a propagação do `dblclick` do echarts não é trivial em treemaps,
 * então mantemos a estratégia atual de timer com 450ms — single click aplica zoom, double
 * click delega ao ColorPickerManager.
 */
class TreeMapChart extends React.Component {
  clickTimer = null;

  constructor(props) {
    super(props);
    const { error } = this.props;
    this.configurationError = error;
    this.colorPickerRef = React.createRef();
    this.manager = useChartManagement(this);
  }

  componentWillUnmount() {
    if (this.clickTimer) {
      clearTimeout(this.clickTimer);
      this.clickTimer = null;
    }
  }

  shouldComponentUpdate(nextProps) {
    return this.manager.changesChecker(nextProps);
  }

  // Caso a api retorne um erro no fetch das configurações de cores, não será possível abrir
  // o color picker e uma mensagem de erro será apresentada ao usuário.
  onConfigurationError() {
    if (!this.configurationError) return;
    toast.error(this.configurationError.toString());
  }

  onChartReady(echarts) {
    this.echarts = echarts;
  }

  zoomToNode = (params) => {
    if (!this.echarts) return;
    const seriesModel = this.echarts.getModel().getSeriesByIndex(params.seriesIndex);
    const node = seriesModel && seriesModel.getData().tree.getNodeByDataIndex(params.dataIndex);
    if (!node) return;
    this.echarts.dispatchAction({
      type: 'treemapZoomToNode',
      seriesIndex: params.seriesIndex,
      targetNode: node,
    });
  };

  // Heurística manual de duplo-clique para conviver com o zoom em clique único.
  // O click do treemap dispara duas vezes no double-click; no segundo click cancelamos
  // o timer do zoom e disparamos o color picker via ColorPickerManager.
  handleNodeClick = (params) => {
    if (this.clickTimer) {
      clearTimeout(this.clickTimer);
      this.clickTimer = null;
      this.manager.handleOnDoubleClick(params);
      return;
    }

    this.clickTimer = setTimeout(() => {
      this.clickTimer = null;
      this.zoomToNode(params);
    }, DOUBLE_CLICK_DELAY_MS);
  };

  render() {
    const { colorPickerColors, chartRef } = this.props;
    const options = this.manager.getOptions();

    if (!options || !options.series) {
      return null;
    }

    return (
      <>
        <ReactEcharts
          option={options}
          onChartReady={(echarts) => this.onChartReady(echarts)}
          ref={(echartsRef) => {
            if (chartRef) chartRef.current = echartsRef;
            this.chartRef = echartsRef;
          }}
          onEvents={{ click: this.handleNodeClick }}
        />
        <ColorPicker
          ref={this.colorPickerRef}
          hideColorPicker={() => this.manager.colorPickerHandler.hideColorPicker()}
          changeBarColor={(newColor) => this.manager.colorPickerHandler.changeColorHandler(newColor)}
          colors={colorPickerColors}
        />
      </>
    );
  }
}

export default TreeMapChart;
